import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form } from 'react-bootstrap';

const BlockInputField = ({ data, elementRef = () => {}, isPreview, error }) => {
    const [values, setValues] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const boxRefs = useRef([]);
    const formatPattern = useMemo(() => data.data?.map((d) => parseInt(d.blockSize)) || [], [data.data]);
    const fieldName = `BlockInputField-${data._id}`;

    const isFieldRequired = data.hasOwnProperty('required') ? data.required : false;
    const allBlocksRequired = data.options?.allBlocksRequired || false;

    const totalBoxes = useMemo(() => formatPattern.reduce((sum, size) => sum + size, 0), [formatPattern]);

    const structuredToFlat = (structuredData) => {
        if (!structuredData || !Array.isArray(structuredData)) return Array(totalBoxes).fill('');

        let flatArray = [];
        data.data?.forEach((block) => {
            const blockKey = block.value;
            const blockData = structuredData.find((b) => b[blockKey]);
            const blockValue = blockData ? blockData[blockKey] : '';

            const chars = blockValue.split('').slice(0, parseInt(block.blockSize));
            flatArray = [...flatArray, ...chars];

            const remaining = parseInt(block.blockSize) - chars.length;
            if (remaining > 0) {
                flatArray = [...flatArray, ...Array(remaining).fill('')];
            }
        });

        return flatArray;
    };

    useEffect(() => {
        let parsedValue = [];
        try {
            parsedValue = data.value ? JSON.parse(data.value) : [];
        } catch {
            parsedValue = [];
        }

        setValues(structuredToFlat(parsedValue));
    }, [data.value, totalBoxes]);

    // Focus handling
    useEffect(() => {
        if (boxRefs.current[focusedIndex]) {
            boxRefs.current[focusedIndex].focus();
        }
    }, [focusedIndex]);

    const handleChange = (index, input) => {
        const updated = [...values];
        let val = input.slice(0, 1);

        if (data.options?.validation === 'numeric') {
            val = val.replace(/\D/g, '');
        } else if (data.options?.validation === 'alpha') {
            val = val.replace(/[^a-zA-Z]/g, '');
        }

        updated[index] = val;
        setValues(updated);

        if (val && index < updated.length - 1) {
            setFocusedIndex(index + 1);
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !values[index] && index > 0) {
            setFocusedIndex(index - 1);
        } else if (e.key === 'ArrowRight' && index < values.length - 1) {
            setFocusedIndex(index + 1);
        } else if (e.key === 'ArrowLeft' && index > 0) {
            setFocusedIndex(index - 1);
        }
    };

    const getStructuredValue = (values) => {
        let boxIndex = 0;
        return data.data?.map((block) => {
            const size = parseInt(block.blockSize);
            const blockValues = values.slice(boxIndex, boxIndex + size);
            boxIndex += size;
            return {
                [block.value]: blockValues.join(''),
            };
        });
    };

    const validateField = (value) => {
        if (!isFieldRequired) return true;
        try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value;
            if (!parsed || parsed.length === 0) return false;

            if (allBlocksRequired) {
                return parsed.every((block) => {
                    const blockValue = Object.values(block)[0];
                    return blockValue && blockValue.length > 0;
                });
            } else {
                return parsed.some((block) => {
                    const blockValue = Object.values(block)[0];
                    return blockValue && blockValue.length > 0;
                });
            }
        } catch {
            return false;
        }
    };

    const renderBoxes = () => {
        let boxIndex = 0;
        return (
            <div className="d-flex flex-wrap">
                {data.data?.map((block, blockIndex) => {
                    const size = parseInt(block.blockSize || 0);
                    return (
                        <div key={`block-${block.value}`} className="d-flex flex-column align-items-center me-3 mb-2">
                            <div className="d-flex align-items-center">
                                {size === 0 && <div style={{ height: '30px' }}></div>}
                                {Array(size)
                                    .fill()
                                    .map((_, i) => {
                                        const currentIndex = boxIndex++;
                                        return (
                                            <Form.Control
                                                key={`box-${currentIndex}`}
                                                type="text"
                                                maxLength="1"
                                                value={values[currentIndex] || ''}
                                                onChange={(e) => handleChange(currentIndex, e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(currentIndex, e)}
                                                onFocus={() => setFocusedIndex(currentIndex)}
                                                ref={(el) => (boxRefs.current[currentIndex] = el)}
                                                className="text-center mx-1"
                                                style={{ width: '30px', height: '30px' }}
                                                disabled={isPreview}
                                            />
                                        );
                                    })}

                                {/* Separator after each block except the last */}
                                {blockIndex !== data.data.length - 1 && <div className="text-muted mx-2">--</div>}
                            </div>
                            {block.text && (
                                <div className="small text-muted mt-1" style={{ marginLeft: '-6px' }}>
                                    {block.text}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="mb-3">
            <div>
                <h5>
                    {data.fieldName}
                    {isFieldRequired && <span className="text-danger">*</span>}
                    {allBlocksRequired && <span className="text-muted small"> (All blocks required)</span>}
                </h5>
                {data.instruction && <pre className="small text-muted">{data.instruction}</pre>}
            </div>

            <Form.Group>
                {renderBoxes()}
                <Form.Control
                    type="hidden"
                    name={fieldName}
                    ref={
                        elementRef &&
                        elementRef({
                            required: isFieldRequired ? 'This field is required' : false,
                            validate: (value) => {
                                if (value === '[]') return !isFieldRequired;
                                try {
                                    const parsed = JSON.parse(value);
                                    return allBlocksRequired
                                        ? parsed.every((block) => {
                                              const blockValue = Object.values(block)[0];
                                              return blockValue && blockValue.length > 0;
                                          })
                                        : parsed.some((block) => {
                                              const blockValue = Object.values(block)[0];
                                              return blockValue && blockValue.length > 0;
                                          });
                                } catch {
                                    return false;
                                }
                            },
                        })
                    }
                    defaultValue={JSON.stringify(getStructuredValue(values))}
                    readOnly
                />
                {error && (
                    <div className="text-danger mt-2">
                        {error.message || (allBlocksRequired ? 'All blocks must be filled' : 'This field is required')}
                    </div>
                )}
            </Form.Group>
        </div>
    );
};

export default React.memo(BlockInputField);
